import { useCallback, useRef, useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/styles/tailwind-utils';
import { AccountLayout } from '@components/layout/AccountLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { Button } from '@components/ui/Button';
import { Modal } from '@components/ui/Modal';
import { mapStaffError, useStaff } from '@features/staff';
import type { InviteStaffRequest, StaffMember, StaffRole, StaffStatus } from '@features/staff';
import { InviteStaffModal } from '@features/staff/components/InviteStaffModal';

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? 'bg-gray-500';
}

function Avatar({ firstName, lastName, photoUrl }: { firstName: string; lastName: string; photoUrl?: string }) {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const color = avatarColor(`${firstName}${lastName}`);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={`${firstName} ${lastName}`}
        className="h-10 w-10 shrink-0 rounded-full object-cover"
      />
    );
  }

  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
        color,
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<StaffStatus, string> = {
  ACTIVE:   'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  INACTIVE: 'bg-gray-100 text-gray-600 ring-gray-500/20',
  INVITED:  'bg-sky-50 text-sky-700 ring-sky-600/20',
  ARCHIVED: 'bg-red-50 text-red-600 ring-red-600/20',
};

function StatusBadge({ status, label }: { status: StaffStatus; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        STATUS_STYLES[status],
      )}
    >
      {label}
    </span>
  );
}

// ─── Role badge ───────────────────────────────────────────────────────────────

const ROLE_STYLES: Record<StaffRole, string> = {
  ADMIN:        'bg-violet-50 text-violet-700 ring-violet-600/20',
  PRACTITIONER: 'bg-teal-50 text-teal-700 ring-teal-600/20',
  FRONTDESK:    'bg-amber-50 text-amber-700 ring-amber-600/20',
};

function RoleBadge({ role, label }: { role: StaffRole; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        ROLE_STYLES[role],
      )}
    >
      {label}
    </span>
  );
}

// ─── Actions dropdown ─────────────────────────────────────────────────────────

interface StaffActionsMenuProps {
  member: StaffMember;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onArchive: () => void;
  onRevokeInvite: () => void;
}

function StaffActionsMenu({
  member,
  onEdit,
  onActivate,
  onDeactivate,
  onArchive,
  onRevokeInvite,
}: StaffActionsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (member.status === 'ARCHIVED') return null;

  function action(fn: () => void) {
    setOpen(false);
    fn();
  }

  return (
    <div ref={ref} className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Actions"
        className="flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-border bg-surface py-1 shadow-lg">
          {member.status !== 'INVITED' && (
            <button
              onClick={() => action(onEdit)}
              className="flex w-full items-center px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              {t('staff.actions.edit')}
            </button>
          )}

          {member.status === 'ACTIVE' && (
            <button
              onClick={() => action(onDeactivate)}
              className="flex w-full items-center px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              {t('staff.actions.deactivate')}
            </button>
          )}

          {member.status === 'INACTIVE' && (
            <button
              onClick={() => action(onActivate)}
              className="flex w-full items-center px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              {t('staff.actions.activate')}
            </button>
          )}

          {(member.status === 'ACTIVE' || member.status === 'INACTIVE') && (
            <button
              onClick={() => action(onArchive)}
              className="flex w-full items-center px-3 py-2 text-sm text-destructive hover:bg-muted"
            >
              {t('staff.actions.archive')}
            </button>
          )}

          {member.status === 'INVITED' && (
            <button
              onClick={() => action(onRevokeInvite)}
              className="flex w-full items-center px-3 py-2 text-sm text-destructive hover:bg-muted"
            >
              {t('staff.actions.revokeInvite')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Staff row ────────────────────────────────────────────────────────────────

interface StaffRowProps {
  member: StaffMember;
  onView: (m: StaffMember) => void;
  onArchive: (m: StaffMember) => void;
  onRevokeInvite: (m: StaffMember) => void;
  onActivate: (m: StaffMember) => void;
  onDeactivate: (m: StaffMember) => void;
}

function StaffRow({ member, onView, onArchive, onRevokeInvite, onActivate, onDeactivate }: StaffRowProps) {
  const { t } = useTranslation();

  const displayName = [member.title, member.firstName, member.lastName].filter(Boolean).join(' ');

  return (
    <li
      className="flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 transition-colors hover:bg-muted/30"
      onClick={() => onView(member)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onView(member);
      }}
    >
      <Avatar
        firstName={member.firstName}
        lastName={member.lastName}
        photoUrl={member.practitionerProfile?.profilePhotoUrl}
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate text-sm font-medium text-foreground">{displayName}</span>
          {member.isOwner && (
            <span className="text-xs text-muted-foreground">({t('staff.owner')})</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span className="truncate">{member.email}</span>
          {member.phone && <span className="hidden sm:inline">{member.phone}</span>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {member.roles.map((role) => (
          <RoleBadge key={role} role={role} label={t(`staff.roles.${role}`)} />
        ))}
        <StatusBadge status={member.status} label={t(`staff.status.${member.status}`)} />
      </div>

      {!member.isOwner && (
        <StaffActionsMenu
          member={member}
          onEdit={() => onView(member)}
          onActivate={() => onActivate(member)}
          onDeactivate={() => onDeactivate(member)}
          onArchive={() => onArchive(member)}
          onRevokeInvite={() => onRevokeInvite(member)}
        />
      )}
    </li>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  isLoading,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            {t('staff.confirmDialog.cancel')}
          </Button>
          <Button type="button" variant="danger" onClick={onConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-foreground">{message}</p>
    </Modal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StaffPage() {
  const { t } = useTranslation();

  const {
    staff,
    isLoading,
    fetchError,
    invite,
    isInviting,
    inviteError,
    resetInviteError,
    updateStatus,
    isUpdatingStatus,
    revokeInvite,
    isRevoking,
  } = useStaff();

  const navigate = useNavigate();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<StaffMember | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<StaffMember | null>(null);

  const handleInviteSubmit = useCallback(
    (data: InviteStaffRequest) => {
      invite(data, { onSuccess: () => setInviteOpen(false) });
    },
    [invite],
  );

  const handleActivate = useCallback(
    (member: StaffMember) => {
      updateStatus({ staffId: member.staffId, status: 'ACTIVE' });
    },
    [updateStatus],
  );

  const handleDeactivate = useCallback(
    (member: StaffMember) => {
      updateStatus({ staffId: member.staffId, status: 'INACTIVE' });
    },
    [updateStatus],
  );

  const handleArchiveConfirm = useCallback(() => {
    if (!confirmArchive) return;
    updateStatus(
      { staffId: confirmArchive.staffId, status: 'ARCHIVED' },
      { onSuccess: () => setConfirmArchive(null) },
    );
  }, [confirmArchive, updateStatus]);

  const handleRevokeConfirm = useCallback(() => {
    if (!confirmRevoke) return;
    revokeInvite(confirmRevoke.staffId, { onSuccess: () => setConfirmRevoke(null) });
  }, [confirmRevoke, revokeInvite]);

  const archiveName = confirmArchive
    ? [confirmArchive.firstName, confirmArchive.lastName].join(' ')
    : '';
  const revokeName = confirmRevoke
    ? [confirmRevoke.firstName, confirmRevoke.lastName].join(' ')
    : '';

  return (
    <AccountLayout>
      <div className="mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              {t('staff.title')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('staff.description')}</p>
          </div>
          <Button onClick={() => { resetInviteError(); setInviteOpen(true); }} size="sm">
            {t('staff.inviteButton')}
          </Button>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <BrandLoader
              title={t('staff.loading.title')}
              subtitle={t('staff.loading.subtitle')}
            />
          ) : fetchError ? (
            <div role="alert" className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
              {mapStaffError(fetchError, t, 'staff.errors.loadFailed')}
            </div>
          ) : staff.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border py-16 text-center">
              <p className="text-base font-medium text-foreground">{t('staff.empty.title')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('staff.empty.description')}</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-md border border-border bg-white/70 shadow-sm">
              {staff.map((member) => (
                <StaffRow
                  key={member.staffId}
                  member={member}
                  onView={(m) => navigate(`/account/staff/${m.staffId}`)}
                  onActivate={handleActivate}
                  onDeactivate={handleDeactivate}
                  onArchive={(m) => setConfirmArchive(m)}
                  onRevokeInvite={(m) => setConfirmRevoke(m)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <InviteStaffModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInviteSubmit}
        isSubmitting={isInviting}
        errorMessage={inviteError ? mapStaffError(inviteError, t, 'staff.errors.inviteFailed') : null}
      />

      <ConfirmDialog
        isOpen={!!confirmArchive}
        title={t('staff.confirmArchive.title')}
        message={t('staff.confirmArchive.message', { name: archiveName })}
        confirmLabel={t('staff.confirmArchive.confirm')}
        onConfirm={handleArchiveConfirm}
        onCancel={() => setConfirmArchive(null)}
        isLoading={isUpdatingStatus}
      />

      <ConfirmDialog
        isOpen={!!confirmRevoke}
        title={t('staff.confirmRevoke.title')}
        message={t('staff.confirmRevoke.message', { name: revokeName })}
        confirmLabel={t('staff.confirmRevoke.confirm')}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setConfirmRevoke(null)}
        isLoading={isRevoking}
      />
    </AccountLayout>
  );
}
